import { z } from 'zod';
import { BoundingBox, Confidence, Id, Timestamp } from './primitives.js';
import { SkuType } from './master.js';
import { PriceObservation } from './vision.js';
/**
 * FR-06: the six correction types the user may apply before submit.
 *
 * TSP J5 asserts all six are exercised and each is logged as training feedback,
 * so the list is exported and the test suite iterates it — a seventh type cannot
 * be added without the journey test noticing.
 */
export const CorrectionType = z.enum([
    'CHANGE_SKU', //        wrong product detected → pick from Master SKU
    'CHANGE_TYPE', //       INTERNAL ⇄ KOMPETITOR
    'CORRECT_PRICE', //     unreadable or wrong price → manual Indonesian-format entry
    'ADJUST_PLANOGRAM', //  facing / sequence / row differ from visual observation
    'ADD_ITEM', //          product present but not detected → pick from Master SKU
    'DELETE_ITEM', //       item detected that is not there
]);
export const CORRECTION_TYPES = CorrectionType.options;
/**
 * Where one product sits on a shelf — the three FR-05 planogram metrics.
 *
 * `sequence` and `row` are nullable because a model that sees a product but
 * cannot place it in the grid says so, and a user correcting the facing count
 * should not be forced to invent a position.
 */
export const PlanogramPlacement = z.object({
    facing: z.number().int().nonnegative(),
    sequence: z.number().int().positive().nullable(),
    row: z.number().int().positive().nullable(),
});
/** The value shape carried by each correction type, discriminated. */
export const CorrectionPayload = z.discriminatedUnion('type', [
    z.object({ type: z.literal('CHANGE_SKU'), priorSkuId: Id.nullable(), correctedSkuId: Id }),
    z.object({ type: z.literal('CHANGE_TYPE'), priorType: SkuType, correctedType: SkuType }),
    z.object({
        type: z.literal('CORRECT_PRICE'),
        priorPrice: PriceObservation.nullable(),
        correctedPrice: PriceObservation,
    }),
    z.object({
        type: z.literal('ADJUST_PLANOGRAM'),
        prior: PlanogramPlacement,
        corrected: PlanogramPlacement,
    }),
    /**
     * A product that was on the shelf and the model never saw.
     *
     * It carries the WHOLE item rather than a SKU and a facing count, because
     * there is no detection row behind it to supply the rest — this payload is the
     * only record that the item exists. `facing` is positive here where
     * `PlanogramPlacement` allows zero: an added item with no facings is not an
     * observation of anything.
     *
     * `addedSkuName` and `addedType` are OPTIONAL ON THE WIRE and written by the
     * server from a `skus` lookup, never trusted from the client — the same rule
     * `CorrectionsService` already applies to model version and prior confidence.
     * Persisted, they make the row self-describing for both the read overlay and
     * the TAD 4.4.3 training export, with no join.
     */
    z.object({
        type: z.literal('ADD_ITEM'),
        addedSkuId: Id,
        placement: PlanogramPlacement.extend({ facing: z.number().int().positive() }),
        price: PriceObservation.nullable(),
        addedSkuName: z.string().max(200).optional(),
        addedType: SkuType.optional(),
    }),
    /**
     * `deletedDetectionId` is null when the item being removed was itself added by
     * a user — it has no detection, and the row it belongs to is named by the
     * correction's `addedItemId` instead.
     */
    z.object({ type: z.literal('DELETE_ITEM'), deletedDetectionId: Id.nullable() }),
]);
/**
 * A single logged correction.
 *
 * TAD 4.5 mandates prior value, corrected value, image region reference, model
 * version, user and timestamp on EVERY correction. That is not audit decoration:
 * from roughly month six these rows are the labelled dataset used to train a
 * dedicated detection model and move planogram inference off the metered cloud
 * API (TAD 4.4.3). The table is designed for that from the first release — the
 * fields below are the reason.
 */
export const Correction = z.object({
    id: Id,
    visitId: Id,
    photoId: Id,
    /** Null for ADD_ITEM, which by definition has no originating detection. */
    detectionId: Id.nullable(),
    /**
     * The ADD_ITEM correction whose item this correction targets, when the thing
     * being corrected is one a user added rather than one the model detected.
     *
     * AN ADDED ITEM'S IDENTITY IS THE ROW THAT CREATED IT. There is no table of
     * user-added items — there is only this table — so the ADD_ITEM row's own id
     * is what a later CHANGE_SKU, ADJUST_PLANOGRAM, CORRECT_PRICE or DELETE_ITEM
     * points at. That keeps the read-side overlay a single rule (latest correction
     * per target per type, where target is `detectionId ?? addedItemId`) and, more
     * importantly, avoids a seventh correction type — TSP J5 asserts there are
     * exactly six.
     *
     * Mutually exclusive with `detectionId`; the database says so
     * (`corrections_target_exclusive`).
     */
    addedItemId: Id.nullable(),
    payload: CorrectionPayload,
    /** Region of the image the correction refers to — the training crop. */
    imageRegion: BoundingBox.nullable(),
    /** Lineage: which model produced the value being corrected. */
    modelVersion: z.string().max(120),
    /** Model's confidence in the value that turned out to be wrong. */
    priorConfidence: Confidence.nullable(),
    correctedByUserId: Id,
    correctedAt: Timestamp,
});
/**
 * A batch of corrections against one photo.
 *
 * Batched because one edit in the UI is several corrections — changing a product
 * usually changes its type too, and adjusting a shelf position while fixing the
 * price is two rows — and sending them together makes them one atomic act.
 *
 * The cap bounds a single HTTP body, not a photo: each ADD_ITEM is its own row,
 * so the number of items a user may add is unbounded.
 */
export const SubmitCorrectionsRequest = z.object({
    photoId: Id,
    corrections: z
        .array(Correction.pick({
        detectionId: true,
        addedItemId: true,
        payload: true,
        imageRegion: true,
        priorConfidence: true,
    }))
        .min(1)
        .max(50),
});
//# sourceMappingURL=correction.js.map